/**
 * Ticket Tiers Manager Component
 * Manages event ticket tiers with capacity tracking
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEventTicketTiers,
  createTicketTier,
  updateTicketTier,
  deleteTicketTier,
  getEventCapacityStats,
} from "../../services/events.service";
import type {
  EventTicketTier,
  CreateTicketTierInput,
} from "../../types/event.types";
import type { UUID } from "../../types/common.types";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TicketIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useToast } from "../ui/Toast";
import LoadingSpinner from "../common/LoadingSpinner";
import Button from "../common/Button";

interface TicketTiersManagerProps {
  eventId: UUID;
  canEdit?: boolean;
}

export default function TicketTiersManager({
  eventId,
  canEdit = true,
}: TicketTiersManagerProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTier, setEditingTier] = useState<EventTicketTier | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "0",
    capacity: "",
  });

  // Fetch ticket tiers
  const { data: tiers = [], isLoading } = useQuery({
    queryKey: ["event-ticket-tiers", eventId],
    queryFn: () => getEventTicketTiers(eventId),
  });

  // Fetch capacity stats
  const { data: capacityStats } = useQuery({
    queryKey: ["event-capacity-stats", eventId],
    queryFn: () => getEventCapacityStats(eventId),
  });

  // Create tier mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateTicketTierInput) => createTicketTier(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-ticket-tiers", eventId],
      });
      showToast("success", "Ticket tier created");
      resetForm();
    },
    onError: (error: Error) => {
      showToast("error", error.message || "Failed to create ticket tier");
    },
  });

  // Update tier mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: Partial<EventTicketTier> }) =>
      updateTicketTier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-ticket-tiers", eventId],
      });
      showToast("success", "Ticket tier updated");
      resetForm();
    },
    onError: (error: Error) => {
      showToast("error", error.message || "Failed to update ticket tier");
    },
  });

  // Delete tier mutation
  const deleteMutation = useMutation({
    mutationFn: (tierId: UUID) => deleteTicketTier(tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-ticket-tiers", eventId],
      });
      showToast("success", "Ticket tier deleted");
    },
    onError: (error: Error) => {
      showToast("error", error.message || "Failed to delete ticket tier");
    },
  });

  const resetForm = () => {
    setFormData({ name: "", description: "", price: "0", capacity: "" });
    setShowAddForm(false);
    setEditingTier(null);
  };

  const handleEdit = (tier: EventTicketTier) => {
    setEditingTier(tier);
    setFormData({
      name: tier.name,
      description: tier.description || "",
      price: tier.price.toString(),
      capacity: tier.capacity?.toString() || "",
    });
    setShowAddForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("error", "Tier name is required");
      return;
    }

    const tierData = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      price: parseFloat(formData.price) || 0,
      capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
    };

    if (editingTier) {
      updateMutation.mutate({ id: editingTier.id, data: tierData });
    } else {
      createMutation.mutate({ ...tierData, event_id: eventId });
    }
  };

  const handleDelete = (tier: EventTicketTier) => {
    if (tier.tickets_sold && tier.tickets_sold > 0) {
      showToast(
        "error",
        "Cannot delete tier with sold tickets. Consider deactivating it instead.",
      );
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${tier.name}"?`)) {
      deleteMutation.mutate(tier.id);
    }
  };

  const toggleTierActive = (tier: EventTicketTier) => {
    updateMutation.mutate({
      id: tier.id,
      data: { is_active: !tier.is_active },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Capacity Overview */}
      {capacityStats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Capacity Overview
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {capacityStats.capacity || "Unlimited"}
              </div>
              <div className="text-sm text-gray-500">Total Capacity</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {capacityStats.registered}
              </div>
              <div className="text-sm text-gray-500">Registered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {capacityStats.available !== null
                  ? capacityStats.available
                  : "N/A"}
              </div>
              <div className="text-sm text-gray-500">Available</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {capacityStats.waitlisted}
              </div>
              <div className="text-sm text-gray-500">Waitlisted</div>
            </div>
          </div>

          {/* Progress Bar */}
          {capacityStats.capacity && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>
                  {Math.round(
                    (capacityStats.registered / capacityStats.capacity) * 100,
                  )}
                  % Full
                </span>
                <span>
                  {capacityStats.registered} / {capacityStats.capacity}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    capacityStats.registered >= capacityStats.capacity
                      ? "bg-red-500"
                      : capacityStats.registered >= capacityStats.capacity * 0.8
                        ? "bg-yellow-500"
                        : "bg-green-500"
                  }`}
                  style={{
                    width: `${Math.min(
                      (capacityStats.registered / capacityStats.capacity) * 100,
                      100,
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Ticket Tiers</h3>
          <p className="text-sm text-gray-500">
            Create different ticket types for this event
          </p>
        </div>
        {canEdit && !showAddForm && (
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={() => setShowAddForm(true)}
          >
            Add Tier
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && canEdit && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">
            {editingTier ? "Edit Ticket Tier" : "New Ticket Tier"}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tier Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., General Admission, VIP"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacity (optional)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.capacity}
                  onChange={(e) =>
                    setFormData({ ...formData, capacity: e.target.value })
                  }
                  placeholder="Leave empty for unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="e.g., Includes early access"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : editingTier
                    ? "Update Tier"
                    : "Create Tier"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tiers List */}
      {tiers.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <TicketIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-gray-900 font-medium mb-1">No Ticket Tiers</h4>
          <p className="text-sm text-gray-500 mb-4">
            Create ticket tiers to manage different ticket types and capacities
          </p>
          {canEdit && !showAddForm && (
            <Button
              variant="primary"
              icon={PlusIcon}
              onClick={() => setShowAddForm(true)}
            >
              Create First Tier
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-white rounded-lg border p-4 ${
                tier.is_active
                  ? "border-gray-200"
                  : "border-gray-100 bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className={`p-2 rounded-lg ${
                      tier.is_active ? "bg-primary-100" : "bg-gray-200"
                    }`}
                  >
                    <TicketIcon
                      className={`h-5 w-5 ${
                        tier.is_active ? "text-primary-600" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          tier.is_active ? "text-gray-900" : "text-gray-500"
                        }`}
                      >
                        {tier.name}
                      </span>
                      {!tier.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    {tier.description && (
                      <p className="text-sm text-gray-500">
                        {tier.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Price */}
                  <div className="text-right">
                    <div className="font-medium text-gray-900">
                      {tier.price === 0 ? "Free" : `$${tier.price.toFixed(2)}`}
                    </div>
                    <div className="text-xs text-gray-500">Price</div>
                  </div>

                  {/* Capacity */}
                  <div className="text-right">
                    <div className="font-medium text-gray-900">
                      {tier.tickets_sold || 0}
                      {tier.capacity && ` / ${tier.capacity}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {tier.capacity ? "Sold / Capacity" : "Sold (Unlimited)"}
                    </div>
                  </div>

                  {/* Actions */}
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleTierActive(tier)}
                        className={`p-2 rounded-lg transition-colors ${
                          tier.is_active
                            ? "text-green-600 hover:bg-green-50"
                            : "text-gray-400 hover:bg-gray-100"
                        }`}
                        title={tier.is_active ? "Deactivate" : "Activate"}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(tier)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tier)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Capacity Progress */}
              {tier.capacity && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>
                      {tier.tickets_available !== undefined &&
                      tier.tickets_available > 0
                        ? `${tier.tickets_available} spots available`
                        : "Sold out"}
                    </span>
                    <span>
                      {Math.round(
                        ((tier.tickets_sold || 0) / tier.capacity) * 100,
                      )}
                      %
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        tier.tickets_sold && tier.tickets_sold >= tier.capacity
                          ? "bg-red-500"
                          : "bg-primary-600"
                      }`}
                      style={{
                        width: `${Math.min(
                          ((tier.tickets_sold || 0) / tier.capacity) * 100,
                          100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
        <strong>Tip:</strong> Create multiple tiers for different ticket types
        (e.g., General Admission, VIP). Each tier can have its own capacity
        limit.
      </div>
    </div>
  );
}
